import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { useModalDismiss } from "@/hooks/useModalDismiss";
import { legalEntitiesService } from "@/services/legalEntitiesService";
import { profileService } from "@/services/profileService";
import { useKeycloak } from "@/modules/auth/context/KeycloakContext";

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

export const CreateEntityModal: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const { keycloak } = useKeycloak();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupName, setGroupName] = useState<string | null>(null);
  const [form, setForm] = useState({
    displayName: "",
    legalName: "",
    primaryEmail: "",
    primaryPhone: "",
  });

  // Fetch the user's group name to auto-associate new entities
  useEffect(() => {
    if (!open) return;
    const email = keycloak?.tokenParsed?.email;
    if (email) {
      profileService.get(email)
        .then((profile) => setGroupName(profile.groupName))
        .catch(() => {});
    }
  }, [open, keycloak]);

  useModalDismiss(open, onClose);

  if (!open) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!form.displayName.trim()) {
      setError("Company name is required");
      return;
    }

    setLoading(true);
    try {
      await legalEntitiesService.create({
        displayName: form.displayName.trim(),
        legalName: form.legalName.trim() || form.displayName.trim(),
        primaryEmail: form.primaryEmail.trim() || undefined,
        primaryPhone: form.primaryPhone.trim() || undefined,
        // Auto-set groupName from user's existing group so new entities belong to the same group
        groupName: groupName || undefined,
      });
      const email = keycloak?.tokenParsed?.email;
      if (email) profileService.invalidate(email);
      setForm({ displayName: "", legalName: "", primaryEmail: "", primaryPhone: "" });
      onCreated();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to create legal entity");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={onClose} />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-bridge-surface border border-bridge-border rounded-2xl shadow-2xl animate-in fade-in zoom-in-95 duration-200">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-bridge-border/60">
          <div>
            <h2 className="text-lg font-bold text-bridge-fg">Create Legal Entity</h2>
            <p className="text-xs text-bridge-muted mt-0.5">
              Add a new legal entity to your organization
              {groupName && (
                <span className="ml-1 text-bridge-accent">· Group: {groupName}</span>
              )}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-white/5 text-bridge-muted hover:text-bridge-fg transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/20 text-red-400 text-sm rounded-xl">
              {error}
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
              Company Name *
            </label>
            <input
              type="text"
              required
              value={form.displayName}
              onChange={(e) => setForm({ ...form, displayName: e.target.value })}
              className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
              placeholder="Acme Properties Ltd"
            />
          </div>

          <div>
            <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
              Legal Name
            </label>
            <input
              type="text"
              value={form.legalName}
              onChange={(e) => setForm({ ...form, legalName: e.target.value })}
              className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
              placeholder="Same as company name if left blank"
            />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Email
              </label>
              <input
                type="email"
                value={form.primaryEmail}
                onChange={(e) => setForm({ ...form, primaryEmail: e.target.value })}
                className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                placeholder="info@acme.co.uk"
              />
            </div>
            <div>
              <label className="block text-xs font-semibold text-bridge-muted uppercase tracking-wider mb-1.5">
                Phone
              </label>
              <input
                type="tel"
                value={form.primaryPhone}
                onChange={(e) => setForm({ ...form, primaryPhone: e.target.value })}
                className="w-full px-4 py-2.5 bg-bridge-bg border border-bridge-border rounded-xl text-sm text-bridge-fg placeholder:text-bridge-subtle focus:ring-2 focus:ring-bridge-accent/20 focus:border-bridge-accent outline-none transition-all"
                placeholder="+44 20 1234 5678"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-bridge-muted hover:text-bridge-fg bg-transparent hover:bg-white/5 border border-bridge-border/70 rounded-xl transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-5 py-2 text-sm font-bold text-white bg-bridge-accent hover:bg-bridge-accentSoft rounded-xl shadow-lg shadow-bridge-accent/20 disabled:opacity-50 transition-all transform active:scale-[0.98]"
            >
              {loading ? "Creating..." : "Create Entity"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
